import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useAuth } from '@/features/auth/AuthProvider';
import { guardarEnCache, leerDeCache } from '@/core/offline/almacen';
import { fetchMyOrganizations, type OrganizationMembership } from './organizations';

const STORAGE_KEY = 'gv.activeOrg';

interface OrgState {
  organizations: OrganizationMembership[];
  activeOrg: OrganizationMembership | null;
  setActiveOrg: (id: string) => void;
  loading: boolean;
  refresh: () => Promise<void>;
}

const OrgContext = createContext<OrgState | null>(null);

export function OrgProvider({ children }: { children: ReactNode }) {
  const { session } = useAuth();
  const [organizations, setOrganizations] = useState<OrganizationMembership[]>([]);
  const [activeId, setActiveId] = useState<string | null>(() => localStorage.getItem(STORAGE_KEY));
  const [loading, setLoading] = useState(true);

  /**
   * Traer las organizaciones, y sobrevivir a no poder.
   *
   * Esto era una llamada a la red sin red de contención: sin señal la promesa
   * se rechazaba, `loading` quedaba en `true` para siempre y `activeOrg` en
   * `null`. Como casi toda pantalla arranca con `if (!activeOrg) return null`,
   * la aplicación se veía **en blanco** —y la cola recibía `organizationId:
   * null`, así que ni siquiera guardaba lo que se cargaba—.
   *
   * Ahora lo que llega del servidor se guarda en el dispositivo, y cuando el
   * servidor no está se usa esa copia. `is_admin` viene ahí adentro: sirve para
   * NO ofrecer lo que no corresponde, nunca para autorizar. Quien autoriza es
   * RLS, del otro lado, y vuelve a comprobarlo en cada operación —incluidas las
   * que se sincronizan después de estar sin señal.
   */
  const traer = async (): Promise<OrganizationMembership[]> => {
    if (!session) return [];

    const clave = `organizaciones:${session.user.id}`;
    try {
      const orgs = await fetchMyOrganizations();
      void guardarEnCache(clave, orgs);
      return orgs;
    } catch (e) {
      const guardadas = await leerDeCache<OrganizationMembership[]>(clave).catch(() => null);
      if (guardadas) return guardadas.valor;
      // Sin copia local no hay nada que mostrar. Se devuelve vacío y la
      // pantalla lo dirá; lo que no puede pasar es quedarse cargando para
      // siempre.
      if (import.meta.env.DEV) console.warn('No se pudieron traer las organizaciones', e);
      return [];
    }
  };

  const load = async () => {
    setOrganizations(await traer());
    setLoading(false);
  };

  useEffect(() => {
    let cancelado = false;

    void (async () => {
      const orgs = await traer();
      if (cancelado) return;
      setOrganizations(orgs);
      setLoading(false);
    })();

    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  // La organización activa guardada solo vale si sigue siendo una membresía real.
  // Es una conveniencia de UI: la seguridad la aplica RLS, no este valor.
  const activeOrg = useMemo(() => {
    if (organizations.length === 0) return null;
    return organizations.find((o) => o.organization_id === activeId) ?? organizations[0] ?? null;
  }, [organizations, activeId]);

  useEffect(() => {
    if (activeOrg) localStorage.setItem(STORAGE_KEY, activeOrg.organization_id);
  }, [activeOrg]);

  const value = useMemo<OrgState>(
    () => ({
      organizations,
      activeOrg,
      loading,
      setActiveOrg: (id: string) => setActiveId(id),
      refresh: load,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [organizations, activeOrg, loading],
  );

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

/**
 * La organización activa, si es que hay una.
 *
 * `useOrg` revienta fuera del proveedor, y con razón: una pantalla que trabaja
 * con datos de la organización y no tiene proveedor está mal montada, y es
 * mejor que se note. Pero hay usos donde el nombre es un adorno útil y su
 * ausencia no rompe nada —el rótulo de Inicio, la vitrina de diseño—. Para
 * ésos, preguntar sin exigir.
 */
export function useOrgOpcional(): OrgState | null {
  return useContext(OrgContext);
}

export function useOrg(): OrgState {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error('useOrg debe usarse dentro de OrgProvider');
  return ctx;
}
